import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { getUserRole, canAccessAdmin } from '../utils/roles';
import {
  joinAffiliateProgram,
  getAffiliate,
  getReferrals,
  getReferralsCount,
  getCommissions,
  getCommissionsCount,
  getLifetimeSalesTotal,
  getPayouts,
  requestPayout,
  updatePayoutDetails,
  updateCustomSlug,
  updateSupportGroupLink,
  updateAffiliateWhatsapp,
  getResources,
  getAffiliateConfig
} from '../services/affiliateService';
import {
  Banknote, Clock, MousePointerClick, Target, TrendingUp, Link as LinkIcon, Copy, Check, CheckCircle2,
  AlertCircle, Loader2, Ban, Users, ArrowRight, Plus, Download, CreditCard, FileText, Image, Video, Layers,
  Search, ExternalLink, FolderOpen, Lock, Sparkles, Phone, PartyPopper, MessageCircle, ShoppingCart, Wallet, User as UserIcon, RefreshCw
} from 'lucide-react';

// Group support khas EJEN AFFILIATE sahaja.
// (Free trial → OFFICIAL_USER_SUPPORT_GROUP; Premium → whatsappTemplate.js)
const WA_SUPPORT_GROUP = 'https://chat.whatsapp.com/JepQwveqmtF43UPzIUgCGm';

export default function AffiliateView() {
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = location.pathname.split('/')[2] || 'dashboard';

  const user = auth.currentUser;
  const isPrivileged = canAccessAdmin(getUserRole(user?.email));
  const [userTier, setUserTier] = useState(null);
  const canApplyAffiliate = isPrivileged || userTier === 'paid';
  const [affiliate, setAffiliate] = useState(null);
  // ─── Pagination state (pattern sama dgn AdminView Users tab) ───
  const PAGE_SIZE = 50;
  const [referrals, setReferrals] = useState([]);
  const [referralsTotal, setReferralsTotal] = useState(0);
  const [referralsPage, setReferralsPage] = useState(0); // 0-indexed current page
  const [referralsHasMore, setReferralsHasMore] = useState(false);
  const [refreshingReferrals, setRefreshingReferrals] = useState(false);
  const referralsCursorRef = useRef([]); // cursorStack[N] = last doc page N

  const [commissions, setCommissions] = useState([]);
  const [commissionsTotal, setCommissionsTotal] = useState(0);
  const [lifetimeSalesTotal, setLifetimeSalesTotal] = useState(0);
  const [commissionsPage, setCommissionsPage] = useState(0);
  const [commissionsHasMore, setCommissionsHasMore] = useState(false);
  const [refreshingCommissions, setRefreshingCommissions] = useState(false);
  const commissionsCursorRef = useRef([]);
  const [payouts, setPayouts] = useState([]);
  const [resources, setResources] = useState([]);
  const [config, setConfig] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [showWAModal, setShowWAModal] = useState(false);
  const [waNumber, setWaNumber] = useState('');
  const [waError, setWaError] = useState('');
  const [payoutTermsChecked, setPayoutTermsChecked] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [copied, setCopied] = useState(null);
  const [toast, setToast] = useState(null);
  const [bankForm, setBankForm] = useState({ bankName: '', accountNumber: '', accountHolder: '' });
  const [slugInput, setSlugInput] = useState('');
  const [savingSlug, setSavingSlug] = useState(false);
  const [editingSlug, setEditingSlug] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [customLinkInput, setCustomLinkInput] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [supportGroupInput, setSupportGroupInput] = useState('');
  const [savingSupportGroup, setSavingSupportGroup] = useState(false);
  const [editingSupportGroup, setEditingSupportGroup] = useState(false);
  const [waContactInput, setWaContactInput] = useState('');
  const [savingWaContact, setSavingWaContact] = useState(false);
  const [editingWaContact, setEditingWaContact] = useState(false);

  const currencySymbol = 'RM';

  useEffect(() => {
    getAffiliateConfig().then(c => setConfig(c)).catch(() => {});
    getResources().then(r => setResources(r)).catch(() => {});
  }, []);

  // ─── userTier: one-time fetch (tier jarang berubah, refresh via login) ───
  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, 'users', user.uid)).then(snap => {
      if (snap.exists()) setUserTier(snap.data().tier ?? null);
    }).catch(() => {});
  }, [user]);

  // ─── Affiliate profile: one-time fetch (refetch via refreshAffiliate selepas edit) ───
  const refreshAffiliate = async () => {
    if (!user) return;
    const data = await getAffiliate(user.uid);
    setAffiliate(data);
    if (data?.payoutDetails) {
      setBankForm({
        bankName: data.payoutDetails.bankName || '',
        accountNumber: data.payoutDetails.accountNumber || '',
        accountHolder: data.payoutDetails.accountHolder || ''
      });
    }
  };
  useEffect(() => {
    if (!user) { setLoading(false); return; }
    refreshAffiliate().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ─── Page-based fetch (pattern sama AdminView users tab) ───
  // pageIdx 0-indexed. Reset stack bila page 0 atau refresh.
  const fetchReferralsPage = async (pageIdx = 0) => {
    if (!user) return;
    setRefreshingReferrals(true);
    try {
      if (pageIdx === 0) referralsCursorRef.current = [];
      const startAfterDoc = pageIdx > 0 ? referralsCursorRef.current[pageIdx - 1] : null;
      const res = await getReferrals(user.uid, {
        maxResults: PAGE_SIZE,
        startAfterDoc
      });
      setReferrals(res.items);
      setReferralsHasMore(res.hasMore);
      setReferralsPage(pageIdx);
      // Track last doc for next-page cursor
      if (res.lastDoc) referralsCursorRef.current[pageIdx] = res.lastDoc;
      // Refresh total count only on initial load atau refresh
      if (pageIdx === 0) {
        try {
          const count = await getReferralsCount(user.uid);
          setReferralsTotal(count);
        } catch (e) { console.warn('[Affiliate] count failed:', e); }
      }
    } catch (e) {
      console.warn('[Affiliate] fetchReferralsPage failed:', e);
    } finally {
      setRefreshingReferrals(false);
    }
  };

  const fetchCommissionsPage = async (pageIdx = 0) => {
    if (!user) return;
    setRefreshingCommissions(true);
    try {
      if (pageIdx === 0) commissionsCursorRef.current = [];
      const startAfterDoc = pageIdx > 0 ? commissionsCursorRef.current[pageIdx - 1] : null;
      const res = await getCommissions(user.uid, {
        maxResults: PAGE_SIZE,
        startAfterDoc
      });
      setCommissions(res.items);
      setCommissionsHasMore(res.hasMore);
      setCommissionsPage(pageIdx);
      if (res.lastDoc) commissionsCursorRef.current[pageIdx] = res.lastDoc;
      if (pageIdx === 0) {
        try {
          const count = await getCommissionsCount(user.uid);
          setCommissionsTotal(count);
        } catch (e) { console.warn('[Affiliate] count failed:', e); }
      }
    } catch (e) {
      console.warn('[Affiliate] fetchCommissionsPage failed:', e);
    } finally {
      setRefreshingCommissions(false);
    }
  };

  // Initial fetch — call page 0
  useEffect(() => {
    if (!user || !affiliate) return;
    fetchReferralsPage(0);
    fetchCommissionsPage(0);
    getPayouts(user.uid).then(setPayouts).catch(() => {});
    getLifetimeSalesTotal(user.uid).then(setLifetimeSalesTotal).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, affiliate]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSubmitWA = async () => {
    if (!user) return;
    const cleaned = waNumber.replace(/\D/g, '');
    if (cleaned.length < 9 || cleaned.length > 15) {
      setWaError('Nombor WhatsApp tidak sah. Contoh: 0123456789');
      return;
    }
    if (!payoutTermsChecked) {
      setWaError('Sila tandakan kotak persetujuan mengenai tempoh payout untuk meneruskan.');
      return;
    }
    setWaError('');
    setJoining(true);
    try {
      await joinAffiliateProgram(user.uid, user.email, user.displayName, cleaned, payoutTermsChecked);
      await refreshAffiliate();
      setShowWAModal(false);
      setShowWelcome(true);
    } catch (e) {
      setWaError(e.message);
    }
    setJoining(false);
  };

  const handleSaveBank = async () => {
    if (!user) return;
    // Validate bank form fields
    const trimmed = {
      bankName: bankForm.bankName.trim(),
      accountNumber: bankForm.accountNumber.replace(/\s/g, ''),
      accountHolder: bankForm.accountHolder.trim()
    };
    if (!trimmed.bankName) { showToast('Sila masukkan nama bank.', 'error'); return; }
    if (!trimmed.accountNumber || !/^\d{8,20}$/.test(trimmed.accountNumber)) {
      showToast('Nombor akaun tidak sah. Masukkan 8-20 digit nombor sahaja.', 'error'); return;
    }
    if (!trimmed.accountHolder || trimmed.accountHolder.length < 3) {
      showToast('Sila masukkan nama pemegang akaun (min. 3 huruf).', 'error'); return;
    }
    setSavingBank(true);
    try {
      await updatePayoutDetails(user.uid, trimmed);
      await refreshAffiliate();  // refetch sebab takda subscription
      showToast('Maklumat bank telah dikemas kini.');
    } catch (e) {
      showToast(e.message, 'error');
    }
    setSavingBank(false);
  };

  const handleRequestPayout = async () => {
    if (!user) return;
    setRequestingPayout(true);
    try {
      await requestPayout(user.uid);
      showToast('Permintaan payout dihantar! 💰');
      const updated = await getPayouts(user.uid);
      setPayouts(updated);
    } catch (e) {
      showToast(e.message, 'error');
    }
    setRequestingPayout(false);
  };

  const handleSaveSupportGroup = async () => {
    if (!user) return;
    setSavingSupportGroup(true);
    try {
      await updateSupportGroupLink(user.uid, supportGroupInput);
      await refreshAffiliate();
      showToast(supportGroupInput.trim() ? 'Link group disimpan.' : 'Link group dibuang.');
      setEditingSupportGroup(false);
    } catch (e) {
      showToast(e.message, 'error');
    }
    setSavingSupportGroup(false);
  };

  const handleSaveWaContact = async () => {
    if (!user) return;
    setSavingWaContact(true);
    try {
      await updateAffiliateWhatsapp(user.uid, waContactInput);
      await refreshAffiliate();
      showToast('Nombor WhatsApp affiliate dikemas kini.');
      setEditingWaContact(false);
    } catch (e) {
      showToast(e.message, 'error');
    }
    setSavingWaContact(false);
  };

  const pd = affiliate?.payoutDetails || {};
  const bankComplete =
    !!(pd.bankName || '').trim() &&
    /^\d{8,20}$/.test((pd.accountNumber || '').replace(/\s/g, '')) &&
    (pd.accountHolder || '').trim().length >= 3;

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const formatCurrency = (cents) =>
    `${currencySymbol}${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('ms-MY', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const s = status?.toLowerCase() || '';
    let bg = 'bg-slate-100 dark:bg-slate-800/50', text = 'text-slate-600 dark:text-slate-400', icon = Clock;

    if (s === 'approved' || s === 'completed' || s === 'paid' || s === 'converted') {
      bg = 'bg-emerald-100 dark:bg-emerald-900/30'; text = 'text-emerald-700 dark:text-emerald-400'; icon = CheckCircle2;
    } else if (s === 'pending') {
      bg = 'bg-amber-100 dark:bg-amber-900/30'; text = 'text-amber-700 dark:text-amber-400'; icon = Clock;
    } else if (s === 'processing') {
      bg = 'bg-blue-100 dark:bg-blue-900/30'; text = 'text-blue-700 dark:text-blue-400'; icon = Loader2;
    } else if (s === 'failed' || s === 'rejected' || s === 'expired') {
      bg = 'bg-rose-100 dark:bg-rose-900/30'; text = 'text-rose-700 dark:text-rose-400'; icon = Ban;
    }

    const Icon = icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${bg} ${text}`}>
        <Icon className="h-3.5 w-3.5" />
        {s}
      </span>
    );
  };

  const renderWAModal = () => {
    if (!showWAModal) return null;
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !joining && setShowWAModal(false)} />
        <div className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl p-6 space-y-5 animate-in zoom-in-95 fade-in duration-200">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20 shrink-0">
              <Phone className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-bold text-slate-900 dark:text-white">Masukkan Nombor WhatsApp</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                Nombor ini digunakan untuk kelulusan & dimasukkan ke dalam group support affiliate.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Nombor WhatsApp</label>
            <input
              type="tel"
              inputMode="numeric"
              autoFocus
              value={waNumber}
              onChange={(e) => { setWaNumber(e.target.value); if (waError) setWaError(''); }}
              placeholder="Contoh: 0123456789"
              disabled={joining}
              className="w-full h-11 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 disabled:opacity-60"
            />
            {waError && (
              <p className="text-xs text-rose-600 dark:text-rose-400 flex items-center gap-1">
                <AlertCircle className="h-3.5 w-3.5" />
                {waError}
              </p>
            )}
          </div>

          <label className="flex items-start gap-2.5 cursor-pointer select-none rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-3">
            <input
              type="checkbox"
              checked={payoutTermsChecked}
              onChange={(e) => { setPayoutTermsChecked(e.target.checked); if (waError) setWaError(''); }}
              disabled={joining}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 dark:border-slate-600 accent-emerald-600 disabled:opacity-60"
            />
            <span className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              Saya faham bahawa pembayaran komisen (payout) mengambil masa <span className="font-bold">1 hingga 7 hari</span> selepas pelanggan disahkan oleh admin.
            </span>
          </label>

          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={() => setShowWAModal(false)}
              disabled={joining}
              className="flex-1 h-11 px-4 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
            >
              Batal
            </button>
            <button
              onClick={handleSubmitWA}
              disabled={joining}
              className="flex-1 h-11 px-4 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white text-sm font-bold shadow-lg shadow-emerald-500/20 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {joining ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Hantar Permohonan</>}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-50 dark:bg-[#0a0f1c]">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  // ═══ APPLY SCREEN (no affiliate doc yet) ═══
  if (!affiliate) {
    // Non-paid users — show upgrade gate instead of apply button
    if (!canApplyAffiliate) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center p-8 bg-slate-50 dark:bg-[#0a0f1c]">
          <div className="max-w-lg w-full text-center space-y-6">
            <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mx-auto shadow-2xl shadow-amber-500/20 relative">
              <Users className="text-white h-10 w-10" />
              <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-slate-900 border-2 border-slate-50 dark:border-[#0a0f1c] flex items-center justify-center">
                <Lock className="text-amber-400 h-4 w-4" />
              </div>
            </div>
            <div>
              <div className="inline-block mb-3 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-md bg-gradient-to-r from-amber-500 to-orange-500 text-white">
                Eksklusif Paid
              </div>
              <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">Program Affiliate SESAAT</h1>
              <p className="text-slate-500 dark:text-slate-400 mt-2">Jana pendapatan sehingga {config?.commissionRate ? `${config.commissionRate * 100}%` : '25%'} komisen berterusan untuk setiap rujukan yang berjaya.</p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-3 font-semibold">
                Program ini hanya terbuka kepada pengguna lesen berbayar.
              </p>
            </div>
            <div className="flex flex-col gap-3 max-w-sm mx-auto">
              <button
                onClick={() => navigate('/pricing')}
                className="w-full flex items-center justify-center gap-2 px-8 py-3.5 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold rounded-xl shadow-lg shadow-amber-500/20 transition-all"
              >
                <Sparkles className="h-5 w-5" />
                Upgrade ke Lesen Berbayar
              </button>
              <p className="text-[11px] text-slate-400 mt-1">
                Selepas anda mempunyai lesen aktif, butang <span className="font-bold text-slate-500">Mohon Akses Affiliate</span> akan muncul di sini.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="flex-1 flex flex-col items-center justify-center p-8 bg-slate-50 dark:bg-[#0a0f1c]">
          <div className="max-w-lg w-full text-center space-y-6">
            <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mx-auto shadow-2xl shadow-emerald-500/20">
              <Users className="text-white h-10 w-10" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">Program Affiliate SESAAT</h1>
              <p className="text-slate-500 dark:text-slate-400 mt-2">Jana pendapatan sehingga {config?.commissionRate ? `${config.commissionRate * 100}%` : '25%'} komisen berterusan untuk setiap rujukan yang berjaya.</p>
              <p className="text-xs text-slate-400 mt-3">Akses anda akan diaktifkan secara automatik selepas semua maklumat yang diminta diisi dengan lengkap.</p>
            </div>
            <div className="flex flex-col gap-3 max-w-sm mx-auto">
              <button
                onClick={() => { setWaNumber(''); setWaError(''); setPayoutTermsChecked(false); setShowWAModal(true); }}
                className="w-full flex items-center justify-center gap-2 px-8 py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-lg transition-all disabled:opacity-50"
              >
                Mohon Akses Affiliate
              </button>
            </div>
          </div>
        </div>
        {renderWAModal()}
      </>
    );
  }

  // ═══ WELCOME — just submitted application, show WA group CTA ═══
  // status 'active' = auto-approved (client premium); 'pending_access' = tunggu admin
  if (showWelcome && (affiliate.status === 'pending_access' || affiliate.status === 'active')) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 bg-slate-50 dark:bg-[#0a0f1c]">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mx-auto shadow-2xl shadow-emerald-500/20">
            <PartyPopper className="text-white h-10 w-10" />
          </div>
          <div>
            <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">Tahniah, Ejen Baharu!</h1>
            <p className="text-slate-600 dark:text-slate-300 mt-3 text-sm leading-relaxed">
              Selamat datang menjadi <span className="font-bold text-emerald-600 dark:text-emerald-400">Ejen Affiliate SESAAT</span>.
              {affiliate.status === 'active'
                ? ' Permohonan anda telah diluluskan secara automatik — anda kini boleh mula berkongsi pautan affiliate anda.'
                : ' Permohonan anda sedang diproses oleh admin kami.'}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-4 leading-relaxed">
              Langkah seterusnya: <span className="font-bold">sertai group support WhatsApp</span> untuk dapatkan bahan promosi, tips closing, dan update terkini.
            </p>
          </div>
          <div className="flex flex-col gap-3 max-w-sm mx-auto">
            <a
              href={WA_SUPPORT_GROUP}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 px-8 py-3.5 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold rounded-xl shadow-lg shadow-emerald-500/20 transition-all"
            >
              <MessageCircle className="h-5 w-5" />
              Sertai Group Support WhatsApp
            </a>
            <button
              onClick={() => setShowWelcome(false)}
              className="w-full text-xs font-semibold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors py-2"
            >
              Teruskan ke Dashboard →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ═══ PENDING APPROVAL — waiting for admin ═══
  if (affiliate.status === 'pending_access') {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 bg-slate-50 dark:bg-[#0a0f1c]">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mx-auto shadow-2xl shadow-amber-500/20">
            <Clock className="text-white h-10 w-10" />
          </div>
          <div>
            <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">Menunggu Kelulusan</h1>
            <span className="inline-block mt-2 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-md bg-gradient-to-r from-amber-500 to-orange-500 text-white">Pending</span>
            <p className="text-slate-500 dark:text-slate-400 mt-4">Permohonan anda untuk program affiliate sedang disemak oleh admin. Anda akan dapat akses penuh sebaik sahaja diluluskan.</p>
            <a
              href={WA_SUPPORT_GROUP}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold text-xs rounded-lg shadow-md transition-all"
            >
              <MessageCircle className="h-4 w-4" />
              Sertai Group Support WhatsApp
            </a>
            <p className="text-xs text-slate-400 mt-4">Email: <span className="font-bold">{affiliate.email}</span></p>
            <p className="text-xs text-slate-400">Dihantar pada: {formatDate(affiliate.joinedAt)}</p>
          </div>
        </div>
      </div>
    );
  }

  // ═══ SUSPENDED ═══
  if (affiliate.status === 'suspended') {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 bg-slate-50 dark:bg-[#0a0f1c]">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-rose-400 to-red-500 flex items-center justify-center mx-auto shadow-2xl shadow-red-500/20">
            <Ban className="text-white h-10 w-10" />
          </div>
          <div>
            <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">Akaun Digantung</h1>
            <p className="text-slate-500 dark:text-slate-400 mt-4">Akaun affiliate anda telah digantung. Sila hubungi admin untuk maklumat lanjut.</p>
          </div>
        </div>
      </div>
    );
  }

  const handleSaveSlug = async () => {
    if (!user) return;
    const val = slugInput.trim();
    if (!val) { showToast('Sila masukkan slug.', 'error'); return; }
    setSavingSlug(true);
    try {
      await updateCustomSlug(val);
      await refreshAffiliate();
      showToast('Custom slug berjaya disimpan!');
      setSlugInput('');
      setEditingSlug(false);
    } catch (e) {
      showToast(e.message, 'error');
    }
    setSavingSlug(false);
  };

  const handleSaveName = async () => {
    if (!user) return;
    const val = nameInput.trim();
    if (val.length < 2) { showToast('Nama mesti sekurang-kurangnya 2 aksara.', 'error'); return; }
    if (val.length > 60) { showToast('Nama terlalu panjang (max 60 aksara).', 'error'); return; }
    setSavingName(true);
    try {
      await setDoc(doc(db, 'affiliates', user.uid), { name: val }, { merge: true });
      showToast('Nama paparan berjaya dikemas kini!');
      setEditingName(false);
    } catch (e) {
      showToast(e.message || 'Gagal simpan nama.', 'error');
    }
    setSavingName(false);
  };

  const handleGenerateLink = () => {
    if (!customLinkInput.trim()) { showToast('Sila masukkan URL.', 'error'); return; }
    const slug = affiliate.customSlug || affiliate.refCode;
    if (!slug) { showToast('Tiada ref code.', 'error'); return; }
    try {
      const url = new URL(customLinkInput.trim());
      const allowed = ['sesaat.ai', 'www.sesaat.ai', 'app.sesaat.ai', 'ai8saat.web.app'];
      if (!allowed.some(d => url.hostname.endsWith(d))) {
        showToast('Hanya URL sesaat.ai dibenarkan.', 'error'); return;
      }
      url.searchParams.set('ref', slug);
      setGeneratedLink(url.toString());
    } catch {
      showToast('URL tidak sah.', 'error');
    }
  };

  // ═══ TABS RENDERING ═══
  const refIdentifier = affiliate.customSlug || affiliate.refCode;
  const lifetimeCommission = affiliate.totalEarnings || 0;
  // Lifetime sales dari fetch penuh SEMUA komisen (getLifetimeSalesTotal) —
  // JANGAN kira dari `commissions`: itu page semasa sahaja (50 item), nombor berubah bila tukar page
  const lifetimeSales = lifetimeSalesTotal;
  const stats = {
    totalEarnings: affiliate.totalEarnings || 0,
    pendingEarnings: affiliate.unpaidBalance || 0,
    totalClicks: affiliate.totalClicks || 0,
    totalLeads: affiliate.totalReferrals || 0,
    // Use server-side totalReferrals (unbounded) instead of client-side commissions.length (limit 50)
    conversionRate: affiliate.totalClicks > 0 ? ((affiliate.totalReferrals || 0) / affiliate.totalClicks) * 100 : 0,
    lifetimeSales,
    lifetimeCommission,
    referralLink: refIdentifier ? `https://sesaat.ai/?ref=${refIdentifier}` : '',
    referralCode: refIdentifier || '',
  };

  const renderDashboard = () => (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Dashboard</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Pretasi kempen pemasaran affiliate anda.</p>
      </div>

      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }}>
        <div className="bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 text-white rounded-xl border-0 shadow-lg overflow-hidden relative group">
          <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-2xl -translate-x-full group-hover:translate-x-full" />
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-6 relative z-10 gap-4">
            <div className="flex items-center gap-5">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-md shadow-inner shrink-0">
                <span className="text-2xl font-bold">{currencySymbol}</span>
              </div>
              <div>
                <p className="text-sm text-emerald-50 font-medium tracking-wide">Jana sehingga {config?.commissionRate ? config.commissionRate * 100 : 25}% komisen</p>
                <p className="text-xl font-bold mt-1 tracking-tight">Kongsikan link rujukan anda sekarang!</p>
              </div>
            </div>
            <a
              href={WA_SUPPORT_GROUP}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-5 py-3 bg-white hover:bg-emerald-50 text-emerald-700 font-bold rounded-xl shadow-lg transition-all shrink-0 whitespace-nowrap w-full sm:w-auto"
            >
              <MessageCircle className="h-5 w-5" />
              Sertai Group Support
            </a>
          </div>
        </div>
      </motion.div>

      {affiliate?.pendingAdjustment && (
        <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
          <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-amber-800 dark:text-amber-300">Pelarasan Akaun</p>
            <p className="text-sm text-amber-700 dark:text-amber-400 mt-0.5">
              Baki anda mengandungi tolakan <strong>RM {(affiliate.pendingAdjustment.amount / 100).toFixed(2)}</strong> daripada {affiliate.pendingAdjustment.reason}. Jumlah ini akan ditolak daripada payout seterusnya.
            </p>
          </div>
        </div>
      )}

      {/* Hero Earnings Stats — lifetime sales + lifetime commission */}
      <div className="grid gap-6 md:grid-cols-2">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} whileHover={{ y: -4 }}>
          <div className="relative overflow-hidden rounded-2xl border border-amber-200 dark:border-amber-900/50 bg-gradient-to-br from-amber-50 via-orange-50 to-white dark:from-amber-900/20 dark:via-orange-900/10 dark:to-slate-900 shadow-lg shadow-amber-500/5 p-6">
            <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-gradient-to-br from-amber-400/20 to-orange-500/10 blur-2xl" />
            <div className="relative flex items-start gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-orange-500/30 shrink-0">
                <ShoppingCart className="h-7 w-7 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-black uppercase tracking-widest text-amber-700/70 dark:text-amber-300/70">Jumlah Jualan</p>
                <p className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white mt-1 tracking-tight">{formatCurrency(stats.lifetimeSales)}</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5">Nilai jualan keseluruhan dijana oleh rujukan anda</p>
              </div>
            </div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} whileHover={{ y: -4 }}>
          <div className="relative overflow-hidden rounded-2xl border border-emerald-200 dark:border-emerald-900/50 bg-gradient-to-br from-emerald-50 via-teal-50 to-white dark:from-emerald-900/20 dark:via-teal-900/10 dark:to-slate-900 shadow-lg shadow-emerald-500/5 p-6">
            <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-gradient-to-br from-emerald-400/20 to-teal-500/10 blur-2xl" />
            <div className="relative flex items-start gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/30 shrink-0">
                <Wallet className="h-7 w-7 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700/70 dark:text-emerald-300/70">Jumlah Komisen</p>
                <p className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white mt-1 tracking-tight">{formatCurrency(stats.lifetimeCommission)}</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5">Jumlah komisen dikumpul sepanjang hayat</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
        {[
          { label: 'Baki Tersedia', value: formatCurrency(stats.totalEarnings), icon: Banknote, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500/10' },
          { label: 'Baki Menunggu', value: formatCurrency(stats.pendingEarnings), icon: Clock, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/10' },
          { label: 'Jumlah Klik', value: stats.totalClicks, icon: MousePointerClick, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-500/10' },
          { label: 'Jumlah Rujukan', value: stats.totalLeads, icon: Target, color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-500/10' },
          { label: 'Kadar Tukaran', value: `${stats.conversionRate.toFixed(1)}%`, icon: TrendingUp, color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-500/10' },
        ].map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.1 }} whileHover={{ y: -5 }}>
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-6">
              <div className="flex items-center gap-4">
                <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${stat.bg} shrink-0`}>
                  {stat.icon === Banknote ? <span className={`text-lg font-bold ${stat.color}`}>{currencySymbol}</span> : <stat.icon className={`h-5 w-5 ${stat.color}`} />}
                </div>
                <div>
                  <p className={`text-2xl font-black tracking-tight ${stat.color}`}>{stat.value}</p>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">{stat.label}</p>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
          <div className="p-6 border-b border-slate-100 dark:border-slate-800">
            <h3 className="text-base font-bold flex items-center gap-2"><Target className="h-4 w-4 text-indigo-500" /> Link Mengikut Tujuan</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Pilih link ikut audience — cold (baru kenal), warm (dah yakin), atau hot (nak terus daftar).</p>
          </div>
          <div className="p-6 space-y-5">
            <div className="space-y-3">
              {[
                { key: 'salespage', label: 'Salespage', desc: 'Untuk audience baru — biar baca salespage dulu', url: refIdentifier ? `https://sesaat.ai/?ref=${refIdentifier}` : '', icon: Sparkles, color: 'text-emerald-500' },
                { key: 'pricing-sales', label: 'Pricing (Salespage)', desc: 'Untuk yang dah yakin — terus tengok harga di salespage', url: refIdentifier ? `https://sesaat.ai/pricing?ref=${refIdentifier}` : '', icon: CreditCard, color: 'text-amber-500' },
                { key: 'pricing-app', label: 'Pricing (App)', desc: 'Terus bawa ke app untuk tengok plan & checkout', url: refIdentifier ? `https://app.sesaat.ai/pricing?ref=${refIdentifier}` : '', icon: CreditCard, color: 'text-sky-500' },
                { key: 'signup', label: 'Daftar Terus', desc: 'Untuk hot lead — skip salespage, terus masuk app', url: refIdentifier ? `https://app.sesaat.ai/?ref=${refIdentifier}` : '', icon: ArrowRight, color: 'text-indigo-500' },
              ].map(item => (
                <div key={item.key} className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 bg-slate-50/50 dark:bg-slate-800/30">
                  <div className="flex items-center gap-2 mb-1.5">
                    <item.icon className={`h-4 w-4 ${item.color}`} />
                    <p className="text-sm font-bold text-slate-900 dark:text-white">{item.label}</p>
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-3 leading-relaxed">{item.desc}</p>
                  <div className="flex gap-2">
                    <input readOnly value={item.url} className="flex h-9 w-full min-w-0 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/50" />
                    <button onClick={() => copyToClipboard(item.url, `purpose-${item.key}`)} className="flex items-center justify-center rounded-md bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 h-9 w-9 shrink-0 transition-colors">
                      {copied === `purpose-${item.key}` ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-5 border-t border-slate-100 dark:border-slate-800/50">
              <label className="text-xs font-bold text-slate-500 uppercase">Kod Rujukan (Kupon)</label>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 mb-2">Pelanggan boleh taip kod ni masa checkout — tanpa perlu klik link.</p>
              <div className="flex gap-2">
                <input readOnly value={stats.referralCode} className="flex h-10 w-full min-w-0 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/50" />
                <button onClick={() => copyToClipboard(stats.referralCode, 'code')} className="flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 h-10 w-10 shrink-0 transition-colors">
                  {copied === 'code' ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
          <div className="flex flex-row items-center justify-between p-6 border-b border-slate-100 dark:border-slate-800">
            <div>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">Rujukan Terkini</h3>
              <p className="text-xs text-slate-500 mt-0.5">5 rujukan terakhir anda</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/50">
                <tr>
                  <th className="h-10 px-3 font-bold text-xs text-slate-500 uppercase">Pelanggan</th>
                  <th className="h-10 px-3 font-bold text-xs text-slate-500 uppercase">Jenis</th>
                  <th className="h-10 px-3 font-bold text-xs text-slate-500 uppercase">Status</th>
                  <th className="h-10 px-3 font-bold text-xs text-slate-500 uppercase text-right whitespace-nowrap">Komisen</th>
                </tr>
              </thead>
              <tbody>
                {referrals.slice(0, 5).map((ref) => {
                  const isRenewal = ref.purchaseType === 'renewal';
                  return (
                  <tr key={ref.id} className="border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="py-3 px-3 font-bold text-slate-700 dark:text-slate-300 max-w-[180px]">
                      <p className="truncate">{ref.refereeEmail || 'Tanpa Nama'}</p>
                      <span className="block text-[10px] font-normal text-slate-500 uppercase truncate">{ref.plan} • {formatDate(ref.createdAt)}</span>
                    </td>
                    <td className="py-3 px-3">
                      {isRenewal ? (
                        <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-[10px] font-bold whitespace-nowrap">
                          🔄 Renewal
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold whitespace-nowrap">
                          🆕 Baru
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-3">{getStatusBadge(ref.status)}</td>
                    <td className="py-3 px-3 text-right font-black text-slate-700 dark:text-slate-200 whitespace-nowrap">{formatCurrency(ref.commissionAmount || 0)}</td>
                  </tr>
                  );
                })}
                {referrals.length === 0 && (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-slate-400 text-sm">Belum ada rujukan.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPayouts = () => (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Payouts</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Sejarah pengeluaran dana komisen anda.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { icon: Banknote, value: formatCurrency(stats.totalEarnings), label: 'Jumlah Pendapatan', color: 'text-emerald-600', bg: 'bg-emerald-500/10' },
          { icon: Clock, value: formatCurrency(stats.pendingEarnings), label: 'Boleh Dikeluarkan', color: 'text-blue-600', bg: 'bg-blue-500/10' },
          { icon: CreditCard, value: payouts.length, label: 'Kekerapan Payout', color: 'text-amber-600', bg: 'bg-amber-500/10' },
        ].map((card, i) => (
          <div key={i} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-5 flex items-center gap-4">
            <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${card.bg}`}>
               <card.icon className={`h-5 w-5 ${card.color}`} />
            </div>
            <div>
              <p className={`text-2xl font-black ${card.color}`}>{card.value}</p>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{card.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className={`rounded-xl border p-5 flex items-start justify-between gap-3 ${
        !bankComplete
          ? 'border-amber-200 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-900/10'
          : 'border-indigo-200 dark:border-indigo-900/50 bg-indigo-50/50 dark:bg-indigo-900/10'
      }`}>
        <div className="flex gap-3">
          <AlertCircle className={`h-5 w-5 mt-0.5 shrink-0 ${!bankComplete ? 'text-amber-600' : 'text-indigo-600'}`} />
          <div>
            <p className={`text-sm font-bold ${!bankComplete ? 'text-amber-900 dark:text-amber-400' : 'text-indigo-900 dark:text-indigo-400'}`}>
              {!bankComplete ? 'Maklumat Bank Belum Lengkap' : 'Pengeluaran Automatik / Manual'}
            </p>
            <p className={`text-sm mt-1 ${!bankComplete ? 'text-amber-700 dark:text-amber-300/80' : 'text-indigo-700 dark:text-indigo-300/80'}`}>
              {!bankComplete ? (
                <>Sila lengkapkan nama bank, nombor akaun dan nama pemegang akaun di tab <strong>Settings</strong> sebelum request payout.</>
              ) : (
                <>Minimum pengeluaran adalah {config?.minPayoutThreshold ? formatCurrency(config.minPayoutThreshold) : 'RM50.00'}.
                Pastikan maklumat bank anda sah di tab 'Settings'.</>
              )}
            </p>
          </div>
        </div>
        <button
          onClick={bankComplete ? handleRequestPayout : () => navigate('/affiliate/settings')}
          disabled={requestingPayout || (bankComplete && (affiliate.unpaidBalance || 0) < (config?.minPayoutThreshold || 5000))}
          className={`flex-shrink-0 px-4 py-2 font-bold rounded-lg shadow disabled:opacity-50 text-sm transition-colors ${
            !bankComplete
              ? 'bg-amber-600 hover:bg-amber-700 text-white'
              : 'bg-indigo-600 hover:bg-indigo-700 text-white'
          }`}
        >
          {requestingPayout ? <Loader2 className="h-4 w-4 animate-spin" /> : (!bankComplete ? 'Isi Maklumat Bank' : 'Mohon Pengeluaran')}
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100 dark:border-slate-800">
           <h3 className="text-base font-bold">Sejarah Pengeluaran</h3>
        </div>
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/50">
            <tr>
              <th className="h-10 px-6 font-bold text-xs text-slate-500 uppercase">Tarikh</th>
              <th className="h-10 px-6 font-bold text-xs text-slate-500 uppercase">Kaedah</th>
              <th className="h-10 px-6 font-bold text-xs text-slate-500 uppercase">Status</th>
              <th className="h-10 px-6 font-bold text-xs text-slate-500 uppercase text-right">Jumlah</th>
            </tr>
          </thead>
          <tbody>
            {payouts.map((pOut) => (
              <tr key={pOut.id} className="border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                <td className="p-4 px-6 text-slate-600 dark:text-slate-300 text-sm">{formatDate(pOut.createdAt)}</td>
                <td className="p-4 px-6 text-slate-500 dark:text-slate-400 capitalize">{pOut.method?.replace('_', ' ') || 'Bank Transfer'}</td>
                <td className="p-4 px-6">{getStatusBadge(pOut.status)}</td>
                <td className="p-4 px-6 text-right font-black text-slate-800 dark:text-slate-200">{formatCurrency(pOut.amount)}</td>
              </tr>
            ))}
            {payouts.length === 0 && (
              <tr>
                <td colSpan={4} className="p-8 text-center text-slate-400 text-sm">Tiada sejarah pengeluaran.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderResources = () => {
    const typeIcons = { BANNER: Image, LOGO: Image, VIDEO: Video, EMAIL_TEMPLATE: FileText, DOCUMENT: FileText, OTHER: Layers };
    const typeColors = { BANNER: 'bg-blue-100 text-blue-700', LOGO: 'bg-purple-100 text-purple-700', VIDEO: 'bg-rose-100 text-rose-700', EMAIL_TEMPLATE: 'bg-amber-100 text-amber-700' };

    return (
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Resources</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Muat turun aset visual dan pemasaran rasmi SESAAT.</p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input placeholder="Cari sumber..." className="w-full h-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50" />
          </div>
        </div>

        {resources.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-slate-300 dark:border-slate-800 rounded-xl bg-slate-50/50 dark:bg-slate-900/30">
            <FolderOpen className="h-10 w-10 text-slate-400 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Tiada fail sumber disediakan buat masa ini.</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {resources.map((res) => {
              const Icon = typeIcons[res.type] || Layers;
              const colorClass = typeColors[res.type] || 'bg-slate-100 text-slate-700';
              return (
                <div key={res.id} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm flex flex-col group hover:shadow-md transition-all hover:-translate-y-1">
                  <div className="p-5 flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${colorClass}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <span className="text-[10px] font-bold px-2 py-1 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 uppercase">{res.type?.replace('_', ' ')}</span>
                    </div>
                    <h3 className="text-base font-bold text-slate-900 dark:text-white mb-1">{res.title}</h3>
                    <p className="text-xs text-slate-500 line-clamp-2">{res.description || 'Tiada deskripsi.'}</p>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800 rounded-b-xl flex flex-col gap-3">
                    <div className="flex justify-between text-xs text-slate-500 font-medium">
                      <span className="truncate pr-2">{res.fileName}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400 font-bold">{res.downloads || 0} DLs</span>
                      <a href={res.fileUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-xs font-bold shadow-sm hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors text-slate-700 dark:text-slate-200">
                        <Download className="h-3.5 w-3.5" /> Download
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderReferrals = () => (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Senarai Rujukan</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Pantau klik dan penukaran (conversion) yang berjaya.</p>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30 gap-3 flex-wrap">
           <h3 className="text-base font-bold">
              Semua Rujukan
              <span className="text-xs font-normal text-slate-500 ml-2">
                ({referralsTotal} total)
              </span>
           </h3>
           <button
             type="button"
             onClick={() => fetchReferralsPage(0)}
             disabled={refreshingReferrals}
             className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-bold disabled:opacity-50"
           >
             <RefreshCw size={12} className={refreshingReferrals ? 'animate-spin' : ''} />
             Refresh
           </button>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/80">
            <tr>
              <th className="h-10 px-3 font-bold text-xs text-slate-500 uppercase">Pelanggan</th>
              <th className="h-10 px-3 font-bold text-xs text-slate-500 uppercase">Jenis</th>
              <th className="h-10 px-3 font-bold text-xs text-slate-500 uppercase">Status</th>
              <th className="h-10 px-3 font-bold text-xs text-slate-500 uppercase text-right whitespace-nowrap">Komisen</th>
            </tr>
          </thead>
          <tbody>
            {referrals.map((ref) => {
              const isRenewal = ref.purchaseType === 'renewal';
              return (
              <tr key={ref.id} className="border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                <td className="py-3 px-3 font-bold text-slate-700 dark:text-slate-300 max-w-[220px]">
                  <p className="truncate">{ref.refereeEmail || 'Tanpa Nama'}</p>
                  <span className="block text-[10px] font-normal text-slate-500 uppercase truncate">{ref.plan} • {formatDate(ref.createdAt)}</span>
                </td>
                <td className="py-3 px-3">
                  {isRenewal ? (
                    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-[10px] font-bold whitespace-nowrap">
                      🔄 Renewal
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold whitespace-nowrap">
                      🆕 Baru
                    </span>
                  )}
                </td>
                <td className="py-3 px-3">{getStatusBadge(ref.status)}</td>
                <td className="py-3 px-3 text-right font-black text-slate-800 dark:text-slate-200 whitespace-nowrap">{formatCurrency(ref.commissionAmount || 0)}</td>
              </tr>
              );
            })}
            {referrals.length === 0 && (
              <tr>
                <td colSpan={4} className="p-8 text-center text-slate-400 text-sm">Tiada rujukan didapati.</td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
        {(referralsTotal > PAGE_SIZE || referralsPage > 0) && (() => {
          const totalPages = Math.max(1, Math.ceil(referralsTotal / PAGE_SIZE));
          return (
            <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-xs text-slate-400">
                Halaman {referralsPage + 1} — papar {referrals.length} rujukan{referralsTotal > 0 && <> daripada <b>{referralsTotal}</b> total</>}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => fetchReferralsPage(0)}
                  disabled={refreshingReferrals || referralsPage === 0}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-200 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  ← Pertama
                </button>
                <button
                  onClick={() => referralsPage > 0 && fetchReferralsPage(referralsPage - 1)}
                  disabled={refreshingReferrals || referralsPage === 0}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-200 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Sebelum
                </button>
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 px-2">
                  {referralsPage + 1}{referralsTotal ? ` / ${totalPages}` : ''}
                </span>
                <button
                  onClick={() => referralsHasMore && fetchReferralsPage(referralsPage + 1)}
                  disabled={refreshingReferrals || !referralsHasMore}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Seterusnya →
                </button>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Commissions breakdown if any */}
      {commissions.length > 0 && (() => {
        const referralEmailById = referrals.reduce((acc, r) => {
          acc[r.id] = r.refereeEmail;
          return acc;
        }, {});
        // BARU: lookup purchaseType dari referrals via referralId
        const referralPurchaseTypeById = referrals.reduce((acc, r) => {
          acc[r.id] = r.purchaseType || 'new';
          return acc;
        }, {});
        return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden mt-8">
          <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30 gap-3 flex-wrap">
            <h3 className="text-base font-bold">
              Rekod Komisen
              <span className="text-xs font-normal text-slate-500 ml-2">
                ({commissionsTotal} total)
              </span>
            </h3>
            <button
              type="button"
              onClick={() => fetchCommissionsPage(0)}
              disabled={refreshingCommissions}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-bold disabled:opacity-50"
            >
              <RefreshCw size={12} className={refreshingCommissions ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/80">
              <tr>
                <th className="h-10 px-3 font-bold text-xs text-slate-500 uppercase">Pelanggan</th>
                <th className="h-10 px-3 font-bold text-xs text-slate-500 uppercase">Jenis</th>
                <th className="h-10 px-3 font-bold text-xs text-slate-500 uppercase">Pelan</th>
                <th className="h-10 px-3 font-bold text-xs text-slate-500 uppercase">Status</th>
                <th className="h-10 px-3 font-bold text-xs text-slate-500 uppercase text-right whitespace-nowrap">Komisen</th>
              </tr>
            </thead>
            <tbody>
              {commissions.map((c) => {
                const email = referralEmailById[c.referralId];
                const isRenewal = referralPurchaseTypeById[c.referralId] === 'renewal';
                return (
                <tr key={c.id} className="border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                  <td className="py-3 px-3 font-bold text-slate-700 dark:text-slate-300 max-w-[200px]">
                    <p className="truncate">{email || <span className="text-slate-400 font-normal italic">Tidak diketahui</span>}</p>
                  </td>
                  <td className="py-3 px-3">
                    {isRenewal ? (
                      <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-[10px] font-bold whitespace-nowrap">
                        🔄 Renewal
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold whitespace-nowrap">
                        🆕 Baru
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-3 font-bold text-slate-700 dark:text-slate-300 capitalize">
                    {c.plan}
                    <span className="block text-[10px] font-normal text-slate-500 whitespace-nowrap">{(c.rate * 100).toFixed(0)}% • {formatDate(c.createdAt)}</span>
                  </td>
                  <td className="py-3 px-3">{getStatusBadge(c.status)}</td>
                  <td className="py-3 px-3 text-right font-black text-slate-800 dark:text-slate-200 whitespace-nowrap">{formatCurrency(c.amount)}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          {(commissionsTotal > PAGE_SIZE || commissionsPage > 0) && (() => {
            const totalPages = Math.max(1, Math.ceil(commissionsTotal / PAGE_SIZE));
            return (
              <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-between gap-4 flex-wrap">
                <p className="text-xs text-slate-400">
                  Halaman {commissionsPage + 1} — papar {commissions.length} komisen{commissionsTotal > 0 && <> daripada <b>{commissionsTotal}</b> total</>}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fetchCommissionsPage(0)}
                    disabled={refreshingCommissions || commissionsPage === 0}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-200 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    ← Pertama
                  </button>
                  <button
                    onClick={() => commissionsPage > 0 && fetchCommissionsPage(commissionsPage - 1)}
                    disabled={refreshingCommissions || commissionsPage === 0}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-200 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Sebelum
                  </button>
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 px-2">
                    {commissionsPage + 1}{commissionsTotal ? ` / ${totalPages}` : ''}
                  </span>
                  <button
                    onClick={() => commissionsHasMore && fetchCommissionsPage(commissionsPage + 1)}
                    disabled={refreshingCommissions || !commissionsHasMore}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Seterusnya →
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
        );
      })()}
    </div>
  );

  const renderCustomLink = () => (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Custom Link</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Tukar mana-mana URL sesaat.ai jadi link bertag affiliate kau.</p>
      </div>
      <div className="max-w-xl space-y-4">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">
          <div className="mb-4">
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Paste URL sesaat.ai</label>
            <input
              type="url"
              value={customLinkInput}
              onChange={e => setCustomLinkInput(e.target.value)}
              placeholder="https://sesaat.ai/pricing"
              className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm p-2.5 focus:ring-2 focus:ring-indigo-500/50 text-slate-900 dark:text-slate-100"
            />
          </div>
          <button
            onClick={handleGenerateLink}
            className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg text-sm transition-colors"
          >
            Jana Link
          </button>
        </div>

        {generatedLink && (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-5">
            <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-2">Link Affiliate Kau</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm text-slate-800 dark:text-slate-200 break-all bg-white dark:bg-slate-900 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700">{generatedLink}</code>
              <button
                onClick={() => { navigator.clipboard.writeText(generatedLink); showToast('Disalin!'); }}
                className="shrink-0 p-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                <Copy className="h-4 w-4 text-slate-500" />
              </button>
            </div>
          </div>
        )}

        <div className="bg-slate-100 dark:bg-slate-800/50 rounded-xl p-4 text-xs text-slate-500 dark:text-slate-400 space-y-1">
          <p className="font-bold text-slate-600 dark:text-slate-300">Contoh penggunaan:</p>
          <p>• <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">sesaat.ai?ref={affiliate?.customSlug || affiliate?.refCode}</code></p>
          <p>• <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">sesaat.ai/pricing?ref={affiliate?.customSlug || affiliate?.refCode}</code></p>
          <p>• <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">app.sesaat.ai/pricing?ref={affiliate?.customSlug || affiliate?.refCode}</code></p>
          <p>• <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">app.sesaat.ai?ref={affiliate?.customSlug || affiliate?.refCode}</code></p>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Tetapan</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Urus maklumat bank dan profil affiliate anda.</p>
      </div>

      <div className="max-w-xl space-y-5">
        {/* Display Name */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">
          <h3 className="text-base font-bold mb-1 flex items-center gap-2">
            <UserIcon className="h-5 w-5 text-indigo-500" />
            Nama Paparan
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Nama yang akan dipaparkan kepada klien anda di Profile mereka. Default: nama dari email.</p>
          {!editingName ? (
            <div className="flex items-center justify-between gap-3 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
              <div className="text-sm text-emerald-700 dark:text-emerald-400 font-medium truncate">
                {affiliate?.name || (affiliate?.email ? affiliate.email.split('@')[0] : 'Tiada nama')}
              </div>
              <button
                onClick={() => { setNameInput(affiliate?.name || ''); setEditingName(true); }}
                className="text-xs font-bold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 shrink-0 transition-colors"
              >
                Edit
              </button>
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={nameInput}
                  onChange={e => setNameInput(e.target.value)}
                  placeholder="Contoh: Ahmad Razali"
                  autoFocus
                  maxLength={60}
                  className="flex-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm p-2.5 focus:ring-2 focus:ring-indigo-500/50 text-slate-900 dark:text-slate-100"
                />
                <button
                  onClick={handleSaveName}
                  disabled={savingName}
                  className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold rounded-lg text-sm transition-colors flex items-center gap-1.5"
                >
                  {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Simpan'}
                </button>
                <button
                  onClick={() => { setNameInput(''); setEditingName(false); }}
                  disabled={savingName}
                  className="px-3 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 disabled:opacity-50 text-slate-700 dark:text-slate-300 font-bold rounded-lg text-sm transition-colors"
                >
                  Batal
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-2">Min 2 aksara, max 60. Boleh guna huruf, nombor, ruang.</p>
            </>
          )}
        </div>

        {/* WhatsApp Affiliate */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">
          <h3 className="text-base font-bold mb-1 flex items-center gap-2">
            <Phone className="h-5 w-5 text-emerald-500" />
            Nombor WhatsApp Affiliate
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
            Nombor ini akan dipaparkan kepada bakal pelanggan dan rujukan anda. Boleh berbeza daripada nombor akaun Sesaat anda.
          </p>
          {!editingWaContact ? (
            <div className="flex items-center justify-between gap-3 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
              <div className="text-sm text-emerald-700 dark:text-emerald-400 font-mono truncate">
                {affiliate?.whatsappNumber || <span className="italic text-slate-400">Tiada nombor ditetapkan</span>}
              </div>
              <button
                onClick={() => { setWaContactInput(affiliate?.whatsappNumber || ''); setEditingWaContact(true); }}
                className="text-xs font-bold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 shrink-0 transition-colors"
              >
                Edit
              </button>
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <input
                  type="tel"
                  inputMode="numeric"
                  value={waContactInput}
                  onChange={e => setWaContactInput(e.target.value)}
                  placeholder="Contoh: 0123456789"
                  autoFocus
                  className="flex-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm p-2.5 focus:ring-2 focus:ring-indigo-500/50 text-slate-900 dark:text-slate-100 font-mono"
                />
                <button
                  onClick={handleSaveWaContact}
                  disabled={savingWaContact}
                  className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold rounded-lg text-sm transition-colors flex items-center gap-1.5"
                >
                  {savingWaContact ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Simpan'}
                </button>
                <button
                  onClick={() => { setWaContactInput(''); setEditingWaContact(false); }}
                  disabled={savingWaContact}
                  className="px-3 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 disabled:opacity-50 text-slate-700 dark:text-slate-300 font-bold rounded-lg text-sm transition-colors"
                >
                  Batal
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-2">Hanya digit (9-15 angka). Format Malaysia: 01XXXXXXXX.</p>
            </>
          )}
        </div>

        {/* Custom Slug */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">
          <h3 className="text-base font-bold mb-1 flex items-center gap-2">
            <LinkIcon className="h-5 w-5 text-indigo-500" />
            Custom Link Slug
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Gantikan kod rawak dengan nama kau sendiri. Cth: <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">sesaat.ai/pricing?ref=zuheir</code></p>
          {affiliate?.customSlug && !editingSlug && (
            <div className="flex items-center justify-between gap-3 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
              <div className="text-sm text-emerald-700 dark:text-emerald-400 font-mono truncate">
                Slug semasa: <strong>{affiliate.customSlug}</strong>
              </div>
              <button
                onClick={() => { setSlugInput(''); setEditingSlug(true); }}
                className="text-xs font-bold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 shrink-0 transition-colors"
              >
                Edit
              </button>
            </div>
          )}
          {(editingSlug || !affiliate?.customSlug) && (
            <>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={slugInput}
                  onChange={e => setSlugInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="Contoh: Ahmad"
                  autoFocus
                  className="flex-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm p-2.5 focus:ring-2 focus:ring-indigo-500/50 text-slate-900 dark:text-slate-100 font-mono"
                />
                <button
                  onClick={handleSaveSlug}
                  disabled={savingSlug}
                  className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold rounded-lg text-sm transition-colors flex items-center gap-1.5"
                >
                  {savingSlug ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Simpan'}
                </button>
                {affiliate?.customSlug && (
                  <button
                    onClick={() => { setSlugInput(''); setEditingSlug(false); }}
                    disabled={savingSlug}
                    className="px-3 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 disabled:opacity-50 text-slate-700 dark:text-slate-300 font-bold rounded-lg text-sm transition-colors"
                  >
                    Batal
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-2">Huruf kecil, nombor dan tanda - sahaja. Min 3 aksara.</p>
            </>
          )}
          {Array.isArray(affiliate?.customSlugHistory) && affiliate.customSlugHistory.length > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-200 dark:border-slate-800">
              <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Slug lain (juga berfungsi)</p>
              <ul className="space-y-1">
                {affiliate.customSlugHistory.map((oldSlug) => {
                  const oldUrl = `https://sesaat.ai/?ref=${oldSlug}`;
                  const copyKey = `history-${oldSlug}`;
                  return (
                    <li key={oldSlug} className="flex items-center justify-between gap-3 py-1">
                      <span className="font-mono text-sm text-slate-700 dark:text-slate-300 truncate">{oldSlug}</span>
                      <button
                        onClick={() => copyToClipboard(oldUrl, copyKey)}
                        title={`Copy ${oldUrl}`}
                        className="flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400 shrink-0 transition-colors"
                      >
                        {copied === copyKey ? (
                          <><Check className="h-3.5 w-3.5 text-emerald-500" /> Disalin</>
                        ) : (
                          <><Copy className="h-3.5 w-3.5" /> Salin link</>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        {/* Support Group WhatsApp Link */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">
          <h3 className="text-base font-bold mb-1 flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-emerald-500" />
            Group Sokongan WhatsApp
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
            Letak link group WhatsApp khas untuk newbie yang daftar guna ref kau. User akan diajak join automatik selepas claim 5 coins.
            {affiliate?.stats?.usersJoinedGroupCount > 0 && (
              <span className="block mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-full text-emerald-700 dark:text-emerald-300 font-bold">
                <Users className="h-3 w-3" />
                {affiliate.stats.usersJoinedGroupCount} user dah join group anda
              </span>
            )}
          </p>
          {affiliate?.supportGroupLink && !editingSupportGroup && (
            <div className="flex items-center justify-between gap-3 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
              <a
                href={affiliate.supportGroupLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-emerald-700 dark:text-emerald-400 font-mono truncate hover:underline"
              >
                {affiliate.supportGroupLink}
              </a>
              <button
                onClick={() => { setSupportGroupInput(affiliate.supportGroupLink || ''); setEditingSupportGroup(true); }}
                className="text-xs font-bold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 shrink-0 transition-colors"
              >
                Edit
              </button>
            </div>
          )}
          {(editingSupportGroup || !affiliate?.supportGroupLink) && (
            <>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={supportGroupInput}
                  onChange={e => setSupportGroupInput(e.target.value)}
                  placeholder="https://chat.whatsapp.com/..."
                  className="flex-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm p-2.5 focus:ring-2 focus:ring-emerald-500/50 text-slate-900 dark:text-slate-100 font-mono"
                />
                <button
                  onClick={handleSaveSupportGroup}
                  disabled={savingSupportGroup}
                  className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold rounded-lg text-sm transition-colors flex items-center gap-1.5"
                >
                  {savingSupportGroup ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Simpan'}
                </button>
                {affiliate?.supportGroupLink && (
                  <button
                    onClick={() => { setSupportGroupInput(''); setEditingSupportGroup(false); }}
                    disabled={savingSupportGroup}
                    className="px-3 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 disabled:opacity-50 text-slate-700 dark:text-slate-300 font-bold rounded-lg text-sm transition-colors"
                  >
                    Batal
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-2">Format: <code>https://chat.whatsapp.com/...</code> — kosongkan untuk buang link.</p>
            </>
          )}
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">
          <h3 className="text-base font-bold mb-4 flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-indigo-500" />
            Maklumat Akaun Bank
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Nama Bank</label>
              <input
                type="text"
                value={bankForm.bankName}
                onChange={(e) => setBankForm(p => ({ ...p, bankName: e.target.value }))}
                placeholder="Maybank, CIMB, RHB..."
                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm p-2.5 focus:ring-2 focus:ring-indigo-500/50 text-slate-900 dark:text-slate-100"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Nombor Akaun</label>
              <input
                type="text"
                value={bankForm.accountNumber}
                onChange={(e) => setBankForm(p => ({ ...p, accountNumber: e.target.value }))}
                placeholder="1234567890"
                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm p-2.5 focus:ring-2 focus:ring-indigo-500/50 text-slate-900 dark:text-slate-100"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Nama Pemegang Akaun</label>
              <input
                type="text"
                value={bankForm.accountHolder}
                onChange={(e) => setBankForm(p => ({ ...p, accountHolder: e.target.value }))}
                placeholder="Nama penuh seperti dalam IC"
                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm p-2.5 focus:ring-2 focus:ring-indigo-500/50 text-slate-900 dark:text-slate-100"
              />
            </div>
          </div>
          <div className="mt-6">
            <button
              onClick={handleSaveBank}
              disabled={savingBank}
              className="px-6 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center min-w-[120px]"
            >
              {savingBank ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Simpan Tetapan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (

    <div className="flex-1 overflow-y-auto bg-slate-50/50 dark:bg-[#0a0f1c] px-4 sm:px-6 py-6 lg:px-10 lg:py-10 font-sans text-slate-900 dark:text-slate-100">
      {/* Toast Notification */}
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`fixed top-20 right-4 left-4 sm:left-auto sm:right-8 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-xl text-sm font-bold ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-slate-900 dark:bg-white text-white dark:text-slate-900'}`}
        >
          {toast.type === 'error' ? <Ban className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
          {toast.msg}
        </motion.div>
      )}

      <div className="max-w-6xl mx-auto pb-20">
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'payouts' && renderPayouts()}
        {activeTab === 'resources' && renderResources()}
        {activeTab === 'referrals' && renderReferrals()}
        {activeTab === 'custom-link' && renderCustomLink()}
        {activeTab === 'settings' && renderSettings()}
      </div>
    </div>
  );
}
